import * as React from "react";
import { Flex } from "../flex";
import { DateField } from "./date-field";
import { Calendar } from "./calendar";
import { AriaDatePickerProps, useDatePicker } from "react-aria";
import { DateValue, parseDate } from "@internationalized/date";
import { InternalPopover as Popover } from "../internal-popover";
import { useDatePickerState } from "react-stately";
import { IconButton } from "../button";
import { HiOutlineCalendar } from "react-icons/hi";
import { InputLabel } from "../input-label";
import { InputError } from "../input-error";
import { cx } from "../../utils/dom";
import { useSpace } from "../space/space";
import { useTheme } from "../theme";
import { FieldProps } from "../form/field";

export interface DateInputProps
  extends Omit<
      AriaDatePickerProps<DateValue>,
      "value" | "onChange" | "defaultValue"
    >,
    FieldProps<"input"> {
  numberOfMonths?: number;
  value?: string;
  defaultValue?: string;
  onChange?(value: string | null, dateObject?: DateValue | null): void;
  dropdownIcon?: React.ReactNode;
}

export const DateInput = React.forwardRef<HTMLDivElement, DateInputProps>(
  (
    {
      appearance = "outline",
      size = "md",
      color,
      numberOfMonths,
      radius,
      dropdownIcon = <HiOutlineCalendar size={16} />,
      value,
      defaultValue,
      onChange,
      ...props
    },
    ref
  ) => {
    const space = useSpace();

    function proxyOnChange(date: DateValue) {
      onChange?.(date?.toString(), date);
    }

    const newProps = {
      ...props,
      value: value ? parseDate(value) : null,
      defaultValue: defaultValue ? parseDate(defaultValue) : null,
      onChange: proxyOnChange,
    } as AriaDatePickerProps<DateValue>;

    const state = useDatePickerState(newProps);
    const pickerRef = React.useRef(null);
    const {
      groupProps,
      calendarProps,
      fieldProps,
      buttonProps,
      errorMessageProps,
      labelProps,
    } = useDatePicker(newProps, state, pickerRef);

    const theme = useTheme();

    return (
      <Flex
        data-radius={radius ?? theme?.radius ?? "lg"}
        data-size={size}
        className={cx("p-input-group", space.compactItemWrapperAdjust)}
        stretchX
        stack
        gap={"xs"}
      >
        {props.label && <InputLabel {...labelProps}>{props.label}</InputLabel>}
        <Flex ref={ref} gap={"xl"} stretchX inline stack>
          <Flex
            className={cx(
              "p-input-control",
              "date-picker-trigger",
              space.classNames
            )}
            ref={pickerRef}
            justify={"space-between"}
            align={"center"}
            data-appearance={appearance}
            data-color={color}
            {...groupProps}
          >
            <DateField
              appearance={appearance}
              size={size}
              {...fieldProps}
            ></DateField>
            <IconButton
              size="sm"
              variant="light"
              color={color}
              {...buttonProps}
            >
              {dropdownIcon}
            </IconButton>
          </Flex>

          {state.isOpen && (
            <Popover
              offset={8}
              state={state}
              triggerRef={pickerRef}
              placement="bottom start"
            >
              <Flex
                style={{ maxHeight: "auto" }}
                data-radius={radius ?? theme?.radius ?? "lg"}
              >
                <Calendar
                  color={color}
                  {...calendarProps}
                  numberOfMonths={numberOfMonths}
                ></Calendar>
              </Flex>
            </Popover>
          )}
        </Flex>
        {props.error && (
          <InputError {...errorMessageProps}>{props.error}</InputError>
        )}
      </Flex>
    );
  }
);

DateInput.displayName = "DateInput";
